// CommandPaletteRow.jsx — one result row (icon · label/sublabel · go chevron).
// WL (Fable Hail Mary Wave 5). Presentational only; all interaction lives in the
// parent (aria-activedescendant pattern → the row is a role=option, never
// focused itself). Fixed 3-column grid keeps every row's icon, text and chevron
// perfectly aligned regardless of content length (Gordon's alignment law).

import React from "react";
import styled from "styled-components";
import { colors } from "../../utils";
import { fontFamily, field, radius, brand, spacing } from "../../theme/tokens";
import WheelThumb from "../CreatorAnalytics/WheelThumb";
import { ACTION_ICONS, IconSegment, IconGo } from "./icons";

const ROW_HEIGHT = 52; // ≥44px touch target (exported so the list sizes match)
export const CMD_ROW_HEIGHT = ROW_HEIGHT;

const Row = styled.div`
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) 20px;
  align-items: center;
  column-gap: ${spacing.md}px;
  height: ${ROW_HEIGHT}px;
  padding: 0 ${spacing.md}px;
  /* Register bug #909, nested-radius law: the Dialog is radius.lg(12px) and
     ListShell insets rows by spacing.sm(8px), so the row's hover/active fill
     must be radius.sm(4px) = 12 - 8 to stay concentric with the Dialog's own
     corner — radius.md(8px) overshot that and visibly mismatched it. */
  border-radius: ${radius.sm}px;
  cursor: pointer;
  position: relative;
  color: ${field.ink};
  background: ${(p) => (p.$active ? "#F5F0FE" : "transparent")};
  transition: background-color 120ms ease;

  &:hover {
    background: ${(p) => (p.$active ? "#F5F0FE" : "#F7F8FA")};
  }

  /* Left accent bar on the active row — a non-color-only cue pairs with it. */
  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 8px;
    bottom: 8px;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: ${(p) => (p.$active ? brand.primary : "transparent")};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const IconChip = styled.span`
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${radius.sm}px;
  color: ${(p) => (p.$accent ? brand.primary : field.muted)};
  background: ${(p) => (p.$accent ? "#F1E9FE" : "#F2F4F7")};
  flex: 0 0 auto;
`;

const TextWrap = styled.span`
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Label = styled.span`
  font-family: ${fontFamily};
  font-size: 14px;
  font-weight: 500;
  color: ${field.ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;

  mark {
    background: transparent;
    color: ${brand.primary};
    font-weight: 700;
  }
`;

const Sub = styled.span`
  font-family: ${fontFamily};
  font-size: 12px;
  color: ${field.muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
`;

const Chevron = styled.span`
  color: ${brand.primary};
  display: inline-flex;
  opacity: ${(p) => (p.$active ? 1 : 0)};
  transition: opacity 120ms ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// Render a label with the fuzzy-matched characters wrapped in <mark> for emphasis.
function highlight(label, indices) {
  if (!indices || indices.length === 0) return label;
  const set = new Set(indices);
  const out = [];
  let run = "";
  let marking = false;
  const flush = () => {
    if (!run) return;
    out.push(
      marking ? <mark key={out.length}>{run}</mark> : <React.Fragment key={out.length}>{run}</React.Fragment>
    );
    run = "";
  };
  for (let i = 0; i < label.length; i++) {
    const m = set.has(i);
    if (m !== marking) {
      flush();
      marking = m;
    }
    run += label[i];
  }
  flush();
  return out;
}

const CommandPaletteRow = ({ item, active, indices, id, onSelect, onHover, style }) => {
  const isAction = item.kind === "action";
  const isWheel = item.kind === "wheel";
  const isSegment = item.kind === "segment";
  const ActionIcon = isAction ? ACTION_ICONS[item.iconKey] : null;

  return (
    <Row
      id={id}
      role="option"
      aria-selected={active ? "true" : "false"}
      $active={active}
      style={style}
      onMouseMove={onHover}
      onClick={() => onSelect(item)}
      data-cmdk-row={item.kind}
    >
      {isWheel ? (
        <WheelThumb portrait={item.portrait} name={item.wheelName} size={30} />
      ) : (
        <IconChip $accent={isAction} aria-hidden="true">
          {isAction && ActionIcon ? <ActionIcon size={18} /> : null}
          {isSegment ? <IconSegment size={18} /> : null}
        </IconChip>
      )}

      <TextWrap>
        <Label title={item.label}>{highlight(item.label, indices)}</Label>
        {item.sublabel ? <Sub title={item.sublabel}>{item.sublabel}</Sub> : null}
      </TextWrap>

      <Chevron $active={active} aria-hidden="true">
        <IconGo size={16} />
      </Chevron>
    </Row>
  );
};

export default CommandPaletteRow;
